import {FormPersonalInfo} from "./FormPersonalInfo";
import {FormPersonalInfoActivity} from "./FormPersonalInfoActivity";
import {FormPersonalInfoMobile} from "./FormPersonalInfoMobile";
import {FormPersonalInfoActivityMobile} from "./FormPersonalInfoActivityMobile";
import {Desktop, LandscapePhonesAndSmaller, LandscapeTablet, TabletAndMinis} from "../../../utils/breakpoints";
import {AplicantOptions} from "./ApplicantOptions";
import { useIntl } from "react-intl";
import { useRequestContext } from "../../../hooks/useRequestContext";
import { useEffect, useRef } from "react";
import ClockLoader from "react-spinners/ClockLoader";
import {useState} from 'react';
import Moment from 'moment';
import { WLOCreditAPIService } from "../../../services";
import type { CygnusPersona } from "../../../context/RequestContext";
interface PersonalInfoContainerProps {
    errors?: any;
    values?: any;
    setValues?: (values: any) => void;
    setPopup: React.Dispatch<React.SetStateAction<string>>;
}

export function PersonalInfoContainer(props: PersonalInfoContainerProps) {
    const {errors, values, setValues, setPopup} = props;
    const intl = useIntl();
    const {request, setRequest} = useRequestContext();
    const [isLoading, setIsLoading] = useState(true);


    const lastConsultaRef = useRef<{ num: string; type: string } | null>(null);
    useEffect(() => {
        setIsLoading(true);
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 5000);
        return () => clearTimeout(timer);
    }, []);

    /** Al entrar al paso: consulta por identificación; si existe persona, llena request (personCygnus + personCode) para rellenar formulario y usar código en submit */
    useEffect(() => {
        if (!request.numIdentification || !request.typeIdentification) return;
        const key = { num: request.numIdentification, type: request.typeIdentification };
        if (lastConsultaRef.current?.num === key.num && lastConsultaRef.current?.type === key.type) return;
        lastConsultaRef.current = key;
        WLOCreditAPIService.consultaSolicitudCygnus({
            Pws_Identificacion: request.numIdentification,
            Pws_Tip_Identificacion: request.typeIdentification,
        })
            .then((res) => {
                const datos = res?.payload?.data?.datos_persona?.Datos_persona;
                if (!datos || !Array.isArray(datos) || datos.length === 0) {
                    setRequest({ ...request, personCygnus: undefined, personCode: undefined });
                    return;
                }
                const p = datos[0];
                const personCode = p.Pws_i_codigo != null && p.Pws_i_codigo !== '' ? String(p.Pws_i_codigo) : undefined;
                const mapped: CygnusPersona = {
                    Pws_Identificacion: p.Pws_Identificacion ?? '',
                    Pws_Tip_Identificacion: p.Pws_Tip_Identificacion ?? '',
                    Pws_Tip_Estado: p.Pws_Tip_Estado ?? '',
                    Pws_Tip_Perfil: p.Pws_Tip_Perfil ?? '',
                    Pws_Tip_person: p.Pws_Tip_person ?? '',
                    Pws_Tip_ocupa: p.Pws_Tip_ocupa ?? '',
                    Pws_Nombres: p.Pws_Nombres ?? '',
                    Pws_Apellidos1: p.Pws_Apellidos1 ?? '',
                    Pws_Apellidos2: p.Pws_Apellidos2 ?? '',
                    Pws_Telefono1: p.Pws_Telefono1 ?? '',
                    Pws_Telefono2: p.Pws_Telefono2 ?? '',
                    Pws_Correo: p.Pws_Correo ?? '',
                    Pws_Fec_expe: p.Pws_Fec_expe ?? '',
                    Pws_Lugar_exp: p.Pws_Lugar_exp ?? '',
                    Pws_Fec_nacime: p.Pws_Fec_nacime ?? '',
                    Pws_Estado_Civil: p.Pws_Estado_Civil ?? '',
                    Pws_Direccion_res: p.Pws_Direccion_res ?? '',
                    Pws_Departamento: p.Pws_Departamento ?? '',
                    Pws_Ciudad_res: p.Pws_Ciudad_res ?? '',
                    Pws_Genero: p.Pws_Genero ?? '',
                    Pws_Estrato_per: p.Pws_Estrato_per ?? '',
                    Pws_Per_cargo: p.Pws_Per_cargo ?? '',
                    Pws_Tip_vivienda: p.Pws_Tip_vivienda ?? '',
                    Pws_Niv_estudio: p.Pws_Niv_estudio ?? '',
                    Pws_Nom_empre: p.Pws_Nom_empre ?? '',
                    Pws_fec_ingemp: p.Pws_fec_ingemp ?? '',
                    Pws_Tip_contra: p.Pws_Tip_contra ?? '',
                    Pws_Ant_labo: p.Pws_Ant_labo ?? '',
                    Pws_Car_emp: p.Pws_Car_emp ?? '',
                    Pws_Nom_Jefedi: p.Pws_Nom_Jefedi ?? '',
                    Pws_Direc_emp: p.Pws_Direc_emp ?? '',
                    Pws_Ciud_emp: p.Pws_Ciud_emp ?? '',
                    Pws_tel_emp1: p.Pws_tel_emp1 ?? '',
                    Pws_tel_emp2: p.Pws_tel_emp2 ?? '',
                    Pws_Fideliza: p.Pws_Fideliza ?? '',
                };
                setRequest({
                    ...request,
                    personCygnus: mapped,
                    personCode,
                });
            })
            .catch(() => {
                lastConsultaRef.current = null;
                setRequest({ ...request, personCygnus: undefined, personCode: undefined });
            });
    }, [request.numIdentification, request.typeIdentification, request, setRequest]);
    
    /** Rellena el formulario con personCygnus solo si corresponde al documento actual (evita pegar data de una persona consultada antes) */
    useEffect(() => {
        if (!request.personCygnus || isLoading || !setValues) return;
        const c = request.personCygnus;
        if (request.numIdentification && request.typeIdentification &&
            (c.Pws_Identificacion !== request.numIdentification || c.Pws_Tip_Identificacion !== request.typeIdentification)) {
            return;
        }
        const fecExpe = c.Pws_Fec_expe?.includes('-00') ? c.Pws_Fec_expe.split('-00')[0] : c.Pws_Fec_expe;
        const fecNac = c.Pws_Fec_nacime?.includes('-00') ? c.Pws_Fec_nacime.split('-00')[0] : c.Pws_Fec_nacime;
        const fecIng = c.Pws_fec_ingemp?.includes('-00') ? c.Pws_fec_ingemp.split('-00')[0] : c.Pws_fec_ingemp;
        const patch: Record<string, unknown> = {
            Pws_Ant_labo: c.Pws_Ant_labo ?? '',
            Pws_Apellidos1: c.Pws_Apellidos1 ?? '',
            Pws_Apellidos2: c.Pws_Apellidos2 ?? '',
            Pws_Car_emp: c.Pws_Car_emp ?? '',
            Pws_Ciud_emp: c.Pws_Ciud_emp ?? '',
            Pws_Ciudad_res: c.Pws_Ciudad_res ?? '',
            Pws_Correo: c.Pws_Correo ?? '',
            Pws_Departamento: c.Pws_Departamento ?? '',
            Pws_Direc_emp: c.Pws_Direc_emp ?? '',
            Pws_Direccion_res: request.direcciones
                ? request.direcciones.road + ' ' + request.direcciones.mainno + ' ' + request.direcciones.suffix + ' ' + request.direcciones.secondaryno + ' ' + request.direcciones.suffix2 + ' ' + request.direcciones.complementaryno + ' ' + request.direcciones.Complement + ' ' + request.direcciones.complementaryno2 + ' ' + request.direcciones.Complement3 + ' ' + request.direcciones.complementaryno3 + ' ' + request.direcciones.Neighborhood
                : (c.Pws_Direccion_res ?? ''),
            Pws_Estado_Civil: c.Pws_Estado_Civil ?? '',
            Pws_Estrato_per: c.Pws_Estrato_per ?? '',
            Pws_Fec_expe: fecExpe ? Moment(new Date(fecExpe)).format('yyyy-MM-DD') : '',
            Pws_Fec_nacime: fecNac ? Moment(new Date(fecNac)).format('yyyy-MM-DD') : '',
            Pws_Genero: c.Pws_Genero ?? '',
            Pws_Identificacion: c.Pws_Identificacion ?? '',
            Pws_Lugar_exp: c.Pws_Lugar_exp ?? '',
            Pws_Niv_estudio: c.Pws_Niv_estudio ?? '',
            Pws_Nom_Jefedi: c.Pws_Nom_Jefedi ?? '',
            Pws_Nom_empre: c.Pws_Nom_empre ?? '',
            Pws_Nombres: c.Pws_Nombres ?? '',
            Pws_Tip_contra: c.Pws_Tip_contra ?? '',
            Pws_Per_cargo: c.Pws_Per_cargo ?? '',
            Pws_Telefono1: c.Pws_Telefono1 ?? '',
            Pws_Telefono2: c.Pws_Telefono2 ?? '',
            Pws_Tip_Estado: c.Pws_Tip_Estado ?? '',
            ws_Tip_Perfil: c.Pws_Tip_Perfil ?? '',
            Pws_Tip_Identificacion: c.Pws_Tip_Identificacion ?? '',
            Pws_Tip_person: c.Pws_Tip_person ?? '',
            Pws_Tip_vivienda: c.Pws_Tip_vivienda ?? '',
            Pws_fec_ingemp: fecIng ? Moment(new Date(fecIng)).format('yyyy-MM-DD') : '',
            Pws_tel_emp1: c.Pws_tel_emp1 ?? '',
            Pws_tel_emp2: c.Pws_tel_emp2 ?? '',
        };
        setValues((prev: any) => ({ ...prev, ...patch }));
    }, [request.personCygnus, request.direcciones, isLoading, setValues]);
    const renderTitle = () => {
        return ( 
            <>
             <ClockLoader id='spinner' color={"#4FAE32"} loading={isLoading} size={100}/>
                {/* <ClockLoader className={'mainspinner'} color="#d72222" loading={isLoading} size={100} /> */}
                <h1 className="title-red">{intl.formatMessage({id: "form_applicant_information_title"})}</h1>
                <h1 className="subtitle-medium">{intl.formatMessage({id: "form_credit_information_subtitle"})}</h1>
                <br />
            </>
            
        )
    }

    return (
        <>
            <Desktop>
                {renderTitle()}
                <AplicantOptions values={values} errors={errors} pwsCodCredi={values.pws_cod_credi} />
                <FormPersonalInfo values={values} errors={errors} setPopup={setPopup} />
                <FormPersonalInfoActivity values={values} errors={errors}/>
            </Desktop>
            <LandscapeTablet>
            {renderTitle()}
                <AplicantOptions values={values} errors={errors} pwsCodCredi={values.pws_cod_credi}/>
                <FormPersonalInfo values={values} errors={errors} setPopup={setPopup} />
                <FormPersonalInfoActivity values={values} errors={errors} />
            </LandscapeTablet>
            <LandscapePhonesAndSmaller>
            {renderTitle()}
                <AplicantOptions values={values} errors={errors} pwsCodCredi={values.pws_cod_credi}/>
                <FormPersonalInfoMobile values={values} errors={errors} setPopup={setPopup} />
                <FormPersonalInfoActivityMobile values={values} errors={errors}/>
            </LandscapePhonesAndSmaller>
        </>
    )
}